import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { BellRing, Check, ClipboardCheck, ExternalLink, Inbox, Link2, MailOpen, Paperclip, Reply, ScrollText, Send } from 'lucide-react';
import type { ReviewItem } from '@mailpilot/shared';
import { api } from '../lib/api';
import { qk } from '../lib/keys';
import { displayName, formatAddressList, fullDate, relativeTime } from '../lib/format';
import { useMailboxMap } from '../hooks/useMailboxes';
import {
  AccountBadge,
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Page,
  PageHeader,
  SendStatusChip,
  Skeleton,
  SkeletonLines,
  spring,
  useToast,
} from '../components/ui';

type Filter = 'unreviewed' | 'all';
type ListRes = { items: ReviewItem[] };

const linkBtn =
  'inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-line bg-surface px-4 text-ui font-medium text-ink shadow-sm transition-colors hover:bg-raised';

function useMarkReviewed() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (id: string) => api('POST /api/review/:id/reviewed', { params: { id } }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: qk.reviewRoot });
      const prev = { unreviewed: qc.getQueryData<ListRes>(qk.review('unreviewed')), all: qc.getQueryData<ListRes>(qk.review('all')) };
      const now = new Date().toISOString();
      qc.setQueryData<ListRes>(qk.review('unreviewed'), (p) => (p ? { items: p.items.filter((i) => i.id !== id) } : p));
      qc.setQueryData<ListRes>(qk.review('all'), (p) => (p ? { items: p.items.map((i) => (i.id === id ? { ...i, reviewedAt: now } : i)) } : p));
      return prev;
    },
    onError: (err, _id, prev) => {
      if (prev?.unreviewed) qc.setQueryData(qk.review('unreviewed'), prev.unreviewed);
      if (prev?.all) qc.setQueryData(qk.review('all'), prev.all);
      toast.error(err);
    },
    onSuccess: () => toast.success('Marked as reviewed.'),
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.reviewRoot }),
  });
}

export function ReviewPage() {
  const [filter, setFilter] = useState<Filter>('unreviewed');
  const q = useQuery({ queryKey: qk.review(filter), queryFn: () => api('GET /api/review', { query: { status: filter } }) });
  const markReviewed = useMarkReviewed();
  const mailboxes = useMailboxMap();

  // Animate the staggered entry only on the first successful load, never on refetches.
  const animatedRef = useRef(false);
  const animateEntry = !animatedRef.current;
  useEffect(() => {
    if (q.data) animatedRef.current = true;
  }, [q.data]);

  const items = q.data?.items ?? [];

  return (
    <Page>
      <PageHeader
        tone="lime"
        icon={<ClipboardCheck className="h-5 w-5" />}
        title="AI Review"
        description="Every automatic send, shown with the original email, the exact response, the sending account and the rule that matched."
        actions={
          <Link to="/reminders" className={linkBtn}>
            <BellRing className="h-4 w-4" aria-hidden />
            Reminders
          </Link>
        }
      />

      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div role="radiogroup" aria-label="Filter review items" className="flex flex-wrap items-center gap-1">
            <button type="button" role="radio" aria-checked={filter === 'unreviewed'} className="mp-chip" onClick={() => setFilter('unreviewed')}>
              <Inbox className="h-4 w-4" aria-hidden /> Needs review
            </button>
            <button type="button" role="radio" aria-checked={filter === 'all'} className="mp-chip" onClick={() => setFilter('all')}>
              <Send className="h-4 w-4" aria-hidden /> All sends
            </button>
          </div>
          {q.data && (
            <p className="text-[0.8125rem] text-muted" aria-live="polite">
              {items.length} {filter === 'unreviewed' ? 'awaiting review' : `item${items.length === 1 ? '' : 's'}`}
            </p>
          )}
        </div>

        {q.isPending ? (
          <div className="space-y-5" aria-busy="true" aria-label="Loading review items">
            {[0, 1].map((i) => (
              <Card key={i} className="space-y-4 p-6">
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-28 rounded-full" />
                  <Skeleton className="h-5 w-36 rounded-full" />
                </div>
                <SkeletonLines lines={3} />
                <Skeleton className="h-28 w-full rounded-2xl" />
              </Card>
            ))}
          </div>
        ) : q.isError ? (
          <Card>
            <ErrorState error={q.error} onRetry={() => void q.refetch()} title="Couldn't load the review queue" />
          </Card>
        ) : items.length === 0 ? (
          <Card>
            <EmptyState
              className="py-14"
              icon={<ClipboardCheck className="h-6 w-6" />}
              title={filter === 'unreviewed' ? 'All caught up' : 'No automatic sends yet'}
              description="Automatic sends appear here with the original email, the response, and the matched rule."
              actions={
                <Link to="/settings/responders">
                  <Button variant="primary" icon={<ScrollText className="h-4 w-4" />}>
                    Responder settings
                  </Button>
                </Link>
              }
            />
          </Card>
        ) : (
          <ul className="space-y-5">
            <AnimatePresence initial={false}>
              {items.map((item, i) => (
                <motion.li
                  key={item.id}
                  layout
                  initial={animateEntry ? { opacity: 0, y: 12 } : false}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, height: 0, marginTop: 0, transition: { duration: 0.2 } }}
                  transition={animateEntry ? { ...spring, delay: Math.min(i, 8) * 0.05 } : spring}
                >
                  <ReviewCard
                    item={item}
                    color={mailboxes.get(item.mailboxId)?.color ?? '#94a3b8'}
                    accountLabel={item.mailboxEmail}
                    onReviewed={() => markReviewed.mutate(item.id)}
                    reviewing={markReviewed.isPending && markReviewed.variables === item.id}
                  />
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </Page>
  );
}

function ColumnLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <h3 className="mb-3 flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
      {icon}
      {children}
    </h3>
  );
}

function ReviewCard({
  item,
  color,
  accountLabel,
  onReviewed,
  reviewing,
}: {
  item: ReviewItem;
  color: string;
  accountLabel: string;
  onReviewed: () => void;
  reviewing: boolean;
}) {
  const navigate = useNavigate();
  const om = item.originalMessage;
  const r = item.response;
  const headingId = `review-${item.id}`;

  return (
    <Card aria-labelledby={headingId} role="article" className="hover-lift">
      {/* header strip */}
      <div className="flex flex-wrap items-center gap-2 border-b border-line/60 px-6 py-3.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-primary-soft text-primary-ink">
          {item.kind === 'acknowledgement' ? <MailOpen className="h-4 w-4" aria-hidden /> : <Reply className="h-4 w-4" aria-hidden />}
        </span>
        <span className="text-[0.9375rem] font-semibold text-ink">{item.kind === 'acknowledgement' ? 'Acknowledgement' : 'Auto reply'}</span>
        <AccountBadge color={color} label={accountLabel} className="max-w-[16rem]" />
        {item.ruleName ? (
          <Badge tone="neutral" icon={<ScrollText className="h-3 w-3" aria-hidden />}>
            {item.ruleName}
          </Badge>
        ) : (
          <Badge tone="neutral">No matched rule recorded</Badge>
        )}
        <span className="ml-auto">
          {item.reviewedAt ? (
            <Badge tone="success" icon={<Check className="h-3 w-3" aria-hidden />} title={fullDate(item.reviewedAt)}>
              Reviewed {relativeTime(item.reviewedAt)}
            </Badge>
          ) : (
            <Badge tone="warn">Not reviewed</Badge>
          )}
        </span>
      </div>

      <div className="grid gap-6 p-6 lg:grid-cols-2">
        {/* Original email */}
        <section aria-label="Original email" className="min-w-0">
          <ColumnLabel icon={<Inbox className="h-3.5 w-3.5" aria-hidden />}>Original email</ColumnLabel>
          <div className="flex items-start gap-3">
            <Avatar name={om.from.name} email={om.from.email} size={40} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                <p className="truncate text-[0.9375rem] font-semibold text-ink">{displayName(om.from)}</p>
                <time className="shrink-0 text-xs text-muted" dateTime={om.date} title={fullDate(om.date)}>
                  {relativeTime(om.date)}
                </time>
              </div>
              <p className="truncate text-[0.8125rem] text-muted">{om.from.email}</p>
              <p id={headingId} className="mt-2 font-medium text-ink">
                {om.subject || '(no subject)'}
              </p>
              <p className="mt-1 line-clamp-5 text-[0.8125rem] leading-relaxed text-muted">{om.excerpt}</p>
            </div>
          </div>
        </section>

        {/* Actual response */}
        <section aria-label="Response that was sent" className="min-w-0">
          <ColumnLabel icon={<Send className="h-3.5 w-3.5" aria-hidden />}>Response sent</ColumnLabel>
          <div className="rounded-2xl border border-hairline bg-raised/60 p-4">
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[0.8125rem]">
              <dt className="text-muted">To</dt>
              <dd className="min-w-0 break-words text-ink">{formatAddressList(r.to) || '—'}</dd>
              <dt className="text-muted">Subject</dt>
              <dd className="min-w-0 break-words font-medium text-ink">{r.subject || '(no subject)'}</dd>
            </dl>
            <div className="mt-3 max-h-80 overflow-y-auto whitespace-pre-wrap break-words border-t border-line/60 pt-3 text-[0.875rem] leading-relaxed text-ink scroll-thin">{r.bodyText}</div>
            {r.attachments.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-2 border-t border-line/60 pt-3" aria-label="Attachments">
                {r.attachments.map((a, idx) => (
                  <li key={`${a.name}-${idx}`} className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-hairline bg-surface px-2 py-1 text-xs text-ink">
                    {a.via === 'link' ? <Link2 className="h-3.5 w-3.5 shrink-0 text-primary-ink" aria-hidden /> : <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted" aria-hidden />}
                    <span className="truncate">{a.name}</span>
                    <span className="text-[0.6875rem] text-muted">{a.via === 'link' ? 'protected link' : 'attachment'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      {/* Status + actions */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-line/60 bg-raised/40 px-6 py-4">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <SendStatusChip status={item.status} />
          <p className="min-w-0 text-[0.8125rem] text-muted">{item.deliveryNote}</p>
          <p className="w-full text-xs text-subtle">
            Sent <time dateTime={item.sentAt ?? undefined}>{item.sentAt ? fullDate(item.sentAt) : 'not sent yet'}</time>
            {' · '}
            Reviewed <time dateTime={item.reviewedAt ?? undefined}>{item.reviewedAt ? fullDate(item.reviewedAt) : 'not yet'}</time>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {item.attentionItemId && (
            <Link to="/reminders" className="inline-flex h-10 items-center gap-1.5 rounded-xl px-3 text-[0.8125rem] font-medium text-muted transition-colors hover:bg-raised hover:text-ink">
              <BellRing className="h-4 w-4" aria-hidden />
              Reminder
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            </Link>
          )}
          <Button icon={<MailOpen className="h-4 w-4" />} onClick={() => navigate(`/inbox/${om.threadId}`)}>
            Open thread
          </Button>
          {!item.reviewedAt && (
            <Button variant="primary" icon={<Check className="h-4 w-4" />} loading={reviewing} onClick={onReviewed}>
              Mark reviewed
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
